<template>
    <div class="lib-select"
         :class="{_rightAligned: rightAligned}"
         @keydown.down="goDownOne"
         @keydown.up="goUpOne"
         @keydown="keydown"
    >
        <input v-if="autocomplete" tabindex="-1" class="autocomplete" @input="fillInAutoComplete" :autocomplete="autocomplete">
        <a href="javascript:void(0)" class="selected" :class="{open:open}" @click="open = !open"
           v-clickaway="clickaway">
            <slot name="selected" v-if="value !== null" :option="selected">{{ selected.description }}</slot>
            <span v-else class="_placeholder">{{ placeholder }}</span>
            <i v-if="open" class="fas fa-angle-up"></i>
            <i v-else class="fas fa-angle-down"></i>
            <input type="hidden" v-if="name" :name="name" :required="required" :value="inputValue">
        </a>
        <div class="options" v-if="open">
            <div class="lib-search-field" v-if="showSearch">
                <input type="search"
                       :value="search"
                       @input="debouncedSearch"
                       :placeholder="searchPlaceholder ? searchPlaceholder : 'Search...'"
                       ref="search"
                       @click.stop>
            </div>
            <div class="options-inner" tabindex="-1">
                <a v-for="(option, index) in filtered"
                   href="javascript:void(0)"
                   @click="select(option)"
                   :ref="`option_${index}`"
                   @focus="focusedIndex = index"
                   :class="{isSelected: options.indexOf(option) === value}">
                    <slot name="descriptions" :option="option">{{ option.description }}</slot>
                    <i v-if="options.indexOf(option) === value" class="fa fa-check"></i>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from './plugins/vue-clickaway';
import {debounce} from "../helpers/debounce";

export default {
    name: 'LibSelect',
    mixins: [clickaway],
    props: {
        options: {},
        value: {},
        name: {},
        required: {
            type: Boolean,
            default: false,
        },
        placeholder: {},
        showSearch: {},
        searchPlaceholder: {},
        rightAligned: {},
        autocomplete: {}
    },
    data() {
        return {
            open: false,
            search: '',
            focusedIndex: null,
        };
    },
    methods: {
        debouncedSearch: debounce(function(event) {
            this.focusedIndex = null;
            this.search = event.target.value;
        }, 100),
        select(value) {
            this.open = false;
            this.$emit('input', this.options.indexOf(value));
        },
        clickaway() {
            this.open = false;
        },
        keydown(event) {
            if (!this.open) {
                const keycode = event.keyCode;

                // Taken from https://stackoverflow.com/a/12467610
                const isPrintableCharacter =
                    (keycode > 47 && keycode < 58) || // number keys
                    (keycode > 64 && keycode < 91) || // letter keys
                    (keycode > 95 && keycode < 112) || // numpad keys
                    (keycode > 185 && keycode < 193) || // ;=,-./` (in order)
                    (keycode > 218 && keycode < 223);   // [\]' (in order)

                if (!this.open && isPrintableCharacter) {
                    this.open = true;
                    this.search = String.fromCharCode(event.keyCode);
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        },
        goDownOne(event) {
            if (this.open) {
                let targetIndex = this.focusedIndex + 1;
                if (this.focusedIndex === null || targetIndex >= this.filtered.length) {
                    targetIndex = 0;
                }
                this.$refs[`option_${targetIndex}`][0].focus();
                event.preventDefault();
            }
        },
        goUpOne(event) {
            if (this.open) {
                let targetIndex = this.focusedIndex - 1;
                if (this.focusedIndex === null || targetIndex < 0) {
                    targetIndex = this.filtered.length - 1;
                }
                this.$refs[`option_${targetIndex}`][0].focus();
                event.preventDefault();
            }
        },
        fillInAutoComplete(event) {
            const foundIndex = this.options.findIndex(option =>
                option.value === event.target.value
                || option.description === event.target.value
            );
            if (foundIndex >= 0) {
                this.$emit('input', foundIndex);
            }
        }
    },
    computed: {
        inputValue() {
            if (this.selected) {
                return this.selected.value ? this.selected.value : value;
            } else {
                return "";
            }
        },
        selected() {
            return this.options[this.value];
        },
        filtered() {
            if (!this.showSearch) {
                return this.options;
            } else {
                return this.options.filter(option => option.description.toLowerCase().includes(this.search.toLowerCase()));
            }
        }
    },
    watch: {
        open() {
            this.focusedIndex = null;
            if (!this.open) {
                this.search = '';
            }
            if (this.open && this.showSearch) {
                this.$nextTick(() => {
                    this.$refs.search.focus();
                    if (this.value) {
                        this.$refs[`option_${this.value}`][0].scrollIntoView({block: "nearest", inline: "start"})
                    } else {
                        this.$refs["option_0"][0].scrollIntoView({block: "nearest", inline: "start"})
                    }
                })
            }
        },
        focusedIndex() {
            if (this.focusedIndex !== null) {
                this.$nextTick(() => {
                    this.$refs[`option_${this.focusedIndex}`][0].scrollIntoView({block: "nearest", inline: "start"});
                });
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import "../../styles/prelude/index";

.lib-select {
    position: relative;
    @include form-field-width;

    .lib-search-field {
        input[type="search"] {
            border: 0 !important;
            margin: 0 !important;
            height: 40px !important;
        }
        border-bottom: 1px solid $lightgray;
    }

    &._rightAligned {
        .selected, .options {
            text-align: right;
        }
    }

    .selected {
        display: block;
        position: relative;
        color: $navy;
        background: white;
        border: 1px solid var(--lib-border-color);
        border-radius: 3px;
        padding: 10px 20px;
        padding-right: 40px;
        height: 40px;
        cursor: pointer;

        &.open {
            border: 2px solid var(--lib-focus-border-color);
            margin: -1px;
            height: 42px;
        }

        &:hover {
            border-color: var(--lib-focus-border-color);
            color: $navyhover;
        }

        &:focus-visible, &:focus, &:focus-within {
            border-color: var(--lib-focus-border-color);
            border-width: 2px;
            margin: -1px;
            height: 42px;
            outline: none;
        }

        i {
            color: $darkgray;
            font-size: 16px;
            font-weight: 300;
            position: absolute;
            right: 16px;
        }

        .explanation {
            color: $darkgray;
            font-weight: normal;
            position: absolute;
            left: 16px;
        }

        ._placeholder {
            color: $gray;
        }
    }

    .options {
        position: absolute;
        top: 48px;
        z-index: 10;
        width: 100%;
        background-color: $white;
        border: 2px solid var(--lib-focus-border-color);
        border-radius: 3px;

        .options-inner {
            max-height: 300px;
            overflow-y: auto;
        }

        a {
            display: block;
            position: relative;
            color: $navy;
            padding: 10px 20px;
            padding-right: 30px;
            cursor: pointer;

            &.isSelected {
                color: $blue;

                &:hover {
                    color: $bluehover;
                }
            }

            &:hover, &:focus {
                color: $navyhover;
                background-color: $offwhite;
                outline: none;
            }

            i {
                color: $blue;
                position: absolute;
                right: 5px;
                font-weight: 300;
            }
        }
    }

    .autocomplete {
        // Hide behind the real component
        height: auto;
        position: absolute;
        top: 5px;
        bottom: 5px;
        left: 5px;
        right: 5px;
        z-index: -100;
        opacity: 0; // Just in case
    }
}
</style>